import React, { useState } from "react";
import { ChevronDown, RotateCw, RotateCcw, X, UserPlus, CheckCircle2, Phone, Users, Wand2, Info, Clock } from "lucide-react";
import { StatusPill, dispatchStageColor } from "./shared";
import { AssignDriverModal } from "./AssignDriverModal";
import { NewBookingModal } from "./NewBookingModal";
import { RefundModal } from "./RefundModal";
import { RideDetailsModal } from "./RideDetailsModal";
import { formatUkDateTime, scheduledTime } from "../../lib/formatTime";

const RIDE_FILTERS = ["", "scheduled", "searching", "offered", "accepted", "in_progress", "completed", "cancelled"];

// Formats the lifecycle completion timestamp (drop-off time) for operational
// lookups (lost property, complaints, customer enquiries). Read-only display;
// returns an em-dash for rides that haven't completed.
const fmtCompleted = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d
    .toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    .replace(",", "");
};

/** Full Rides tab — filter chips, desktop table, mobile cards. */
export const RidesPanel = ({ rides, drivers, statusFilter, setStatusFilter, onRedispatch, onForceCancel, onForceComplete, onAssign, onCreateBooking }) => {
  // Pass FULL drivers list (including offline) to the assign modal so the
  // admin control room can see and recover EVERY driver — not just the
  // ones currently online. The eligibility breakdown comes from
  // /admin/driver-eligibility per driver.
  const [assignTarget, setAssignTarget] = useState(null); // ride being assigned
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null); // completed ride being refunded
  const [detailsTarget, setDetailsTarget] = useState(null); // ride being inspected
  const busyDriverIds = new Set(
    (rides || [])
      .filter((r) => ["accepted", "in_progress"].includes(r.status) && r.driver_id)
      .map((r) => r.driver_id),
  );
  return (
    <div className="bg-white" data-testid="rides-panel">
      {/* Filter chips + New Booking */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
        <div className="flex gap-1.5 overflow-x-auto">
          {RIDE_FILTERS.map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 border transition-colors whitespace-nowrap ${
                statusFilter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary"
              }`}
              data-testid={`filter-${s || "all"}`}
            >
              {s ? s.replace("_", " ") : "ALL"}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowNewBooking(true)}
          className="text-xs px-3 py-1.5 bg-[#002FA7] hover:bg-[#001f6e] text-white font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"
          data-testid="new-booking-btn"
        >
          <Phone className="w-3.5 h-3.5" /> New Booking
        </button>
      </div>

      {/* Mobile: cards · Desktop: table */}
      <div className="md:hidden divide-y divide-border" data-testid="rides-cards">
        {rides.slice(0, 50).map((r) => (
          <RideCard key={r.id} ride={r} onRedispatch={onRedispatch} onForceCancel={onForceCancel} onForceComplete={onForceComplete} onOpenAssign={() => setAssignTarget(r)} onOpenRefund={() => setRefundTarget(r)} onOpenDetails={() => setDetailsTarget(r)} />
        ))}
        {rides.length === 0 && <div className="text-center py-12 text-muted-foreground">No rides match.</div>}
      </div>

      <div className="hidden md:block overflow-x-auto" data-testid="rides-table">
        <table className="w-full text-sm font-mono-tab">          <thead>
            <tr className="border-b border-border bg-secondary/30 label-mini text-muted-foreground">
              <th className="text-left px-4 py-2 font-bold">ID</th>
              <th className="text-left px-4 py-2 font-bold">PASSENGER</th>
              <th className="text-left px-4 py-2 font-bold">PAX</th>
              <th className="text-left px-4 py-2 font-bold">FROM → TO</th>
              <th className="text-left px-4 py-2 font-bold">DIST</th>
              <th className="text-left px-4 py-2 font-bold">FARE</th>
              <th className="text-left px-4 py-2 font-bold">PAY</th>
              <th className="text-left px-4 py-2 font-bold">STATUS</th>
              <th className="text-left px-4 py-2 font-bold">COMPLETED</th>
              <th className="text-left px-4 py-2 font-bold">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {rides.slice(0, 50).map((r) => {
              const stuck = ["searching", "offered"].includes(r.status);
              const assignable = ["searching", "offered", "accepted"].includes(r.status);
              const cancellable = !["completed", "cancelled"].includes(r.status);
              // Force-complete is OPERATIONALLY different from force-cancel:
              // it preserves the fare and activates the passenger payment
              // flow. Only valid for `in_progress` rides (server enforces).
              const completable = r.status === "in_progress";
              const refundable = r.status === "completed";
              return (
                <tr key={r.id} className="border-b border-border hover:bg-secondary/30" data-testid={`ride-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {r.id.substring(0, 8)}
                    {r.manual_dispatch && (
                      <span className="ml-1 inline-block text-[9px] font-bold text-[#002FA7]" title="Manually dispatched">M</span>
                    )}
                  </td>
                  <td className="px-4 py-2">{r.passenger_name}
                    {r.ai_drafted && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE] align-middle" data-testid={`ride-ai-drafted-row-${r.id}`} title="Created via the AI Booking Assistant">
                        <Wand2 className="w-2.5 h-2.5" /> AI
                      </span>
                    )}
                    {scheduledTime(r) && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FFFBEB] text-[#B45309] border border-[#FDE68A]" data-testid={`ride-scheduled-${r.id}`} title="Scheduled pickup time (UK local)">
                        <Clock className="w-2.5 h-2.5" /> {formatUkDateTime(scheduledTime(r))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap font-bold" data-testid={`ride-pax-${r.id}`} title="Number of passengers (Condition 15.5)">
                    {r.passenger_count ?? "—"}
                    <span className="text-[10px] font-normal text-muted-foreground ml-1">pax</span>
                  </td>
                  <td className="px-4 py-2 text-xs truncate max-w-xs" data-testid={`ride-route-${r.id}`}>
                    {r.pickup_address} → {r.dropoff_address}
                    {r.stops?.length > 0 && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-[#EFF3FF] text-[#002FA7] border border-[#002FA7] align-middle" data-testid={`ride-stops-badge-${r.id}`} title={r.stops.map((s, i) => `Stop ${i + 1}: ${s.address}`).join("\n")}>
                        {r.stops.length} STOP{r.stops.length > 1 ? "S" : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">{r.distance_mi ?? r.distance_km} mi</td>
                  <td className="px-4 py-2 font-bold">£{r.fare || r.estimated_fare}</td>
                  <td className="px-4 py-2 uppercase text-xs">{r.payment_method}</td>
                  <td className="px-4 py-2">
                    <StatusPill status={r.status} />
                    {r.dispatch_label && (
                      <div
                        className="text-[10px] font-bold uppercase tracking-wider mt-0.5"
                        style={{ color: dispatchStageColor(r.dispatch_stage) }}
                        data-testid={`dispatch-label-${r.id}`}
                      >
                        {r.dispatch_label}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap text-muted-foreground" data-testid={`ride-completed-${r.id}`}>
                    {fmtCompleted(r.completed_at)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1 items-center">
                      <button
                        onClick={() => setDetailsTarget(r)}
                        className="text-xs px-2 py-1 border border-border hover:bg-secondary transition-colors flex items-center gap-1"
                        data-testid={`ride-details-${r.id}`}
                        title="View ride details & handling history"
                      >
                        <Info className="w-3 h-3" />
                      </button>
                      {assignable && (
                        <button
                          onClick={() => setAssignTarget(r)}
                          className="text-xs px-2 py-1 bg-[#002FA7] hover:bg-[#001f6e] text-white transition-colors flex items-center gap-1"
                          data-testid={`assign-${r.id}`}
                          title="Open assign modal"
                        >
                          <UserPlus className="w-3 h-3" />
                          {r.status === "accepted" ? "Reassign" : "Assign"}
                        </button>
                      )}
                      {stuck && (
                        <button onClick={() => onRedispatch(r.id)} className="text-xs px-2 py-1 bg-[#FFC107] hover:bg-[#e0a900] text-black transition-colors flex items-center gap-1" data-testid={`redispatch-${r.id}`} title="Re-dispatch (auto)">
                          <RotateCw className="w-3 h-3" />
                        </button>
                      )}
                      {completable && (
                        <button
                          onClick={() => onForceComplete(r.id)}
                          className="text-xs px-2 py-1 bg-[#16A34A] hover:bg-[#15803D] text-white transition-colors flex items-center gap-1 font-bold"
                          data-testid={`force-complete-${r.id}`}
                          title="Force-complete (preserves fare, activates payment)"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          COMPLETE
                        </button>
                      )}
                      {cancellable && (
                        <button onClick={() => onForceCancel(r.id)} className="text-xs px-2 py-1 border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors" data-testid={`force-cancel-${r.id}`} title="Force-cancel (no payment, no fare)">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                      {refundable && (
                        <button onClick={() => setRefundTarget(r)} className="text-xs px-2 py-1 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white transition-colors flex items-center gap-1" data-testid={`refund-${r.id}`} title="Issue a full or partial refund">
                          <RotateCcw className="w-3 h-3" /> Refund
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rides.length === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-muted-foreground">No rides match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {assignTarget && (
        <AssignDriverModal
          ride={assignTarget}
          drivers={drivers || []}
          onClose={() => setAssignTarget(null)}
          onAssign={onAssign}
        />
      )}

      {showNewBooking && (
        <NewBookingModal
          onClose={() => setShowNewBooking(false)}
          onCreate={onCreateBooking}
        />
      )}

      {refundTarget && (
        <RefundModal
          ride={refundTarget}
          onClose={() => setRefundTarget(null)}
        />
      )}

      {detailsTarget && (
        <RideDetailsModal
          ride={detailsTarget}
          onClose={() => setDetailsTarget(null)}
        />
      )}
    </div>
  );
};

/** Mobile ride card with assign/redispatch/force-complete/force-cancel buttons. */
const RideCard = ({ ride, onRedispatch, onForceCancel, onForceComplete, onOpenAssign, onOpenRefund, onOpenDetails }) => {
  const stuck = ["searching", "offered"].includes(ride.status);
  const assignable = ["searching", "offered", "accepted"].includes(ride.status);
  const cancellable = !["completed", "cancelled"].includes(ride.status);
  // Force-complete preserves fare + activates payment. Only valid for
  // in_progress (server enforces — see /admin/force-complete handler).
  const completable = ride.status === "in_progress";
  const refundable = ride.status === "completed";
  return (
    <div className="p-4 hover:bg-secondary/20 transition-colors" data-testid={`ride-card-${ride.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-sm truncate">{ride.passenger_name}</span>
            <StatusPill status={ride.status} />
            {ride.ai_drafted && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE]"
                data-testid={`ride-ai-drafted-${ride.id}`}
                title="Created via the AI Booking Assistant"
              >
                <Wand2 className="w-2.5 h-2.5" /> AI
              </span>
            )}
            {ride.dispatch_label && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: dispatchStageColor(ride.dispatch_stage) }}
                data-testid={`dispatch-label-mobile-${ride.id}`}
              >
                · {ride.dispatch_label}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground font-mono-tab truncate">{ride.id.substring(0, 8)} · {(ride.payment_method || "—").toUpperCase()}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-heading text-xl font-black">£{ride.fare || ride.estimated_fare}</div>
          <div className="label-mini text-muted-foreground">{ride.distance_mi ?? ride.distance_km} mi</div>
          <button onClick={onOpenDetails} className="mt-1 text-[11px] text-[#002FA7] font-semibold inline-flex items-center gap-1" data-testid={`ride-details-mobile-${ride.id}`}>
            <Info className="w-3 h-3" /> Details
          </button>
        </div>
      </div>
      <div className="space-y-1 text-xs mb-3">
        <div className="flex items-start gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] mt-1 flex-shrink-0" />
          <span className="truncate">{ride.pickup_address}</span>
        </div>
        {(ride.stops || []).map((st, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`ride-stop-mobile-${ride.id}-${i}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#002FA7] mt-1 flex-shrink-0" />
            <span className="truncate">{st.address} <span className="text-[10px] uppercase text-muted-foreground">· stop {i + 1}</span></span>
          </div>
        ))}
        <div className="flex items-start gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] mt-1 flex-shrink-0" />
          <span className="truncate">{ride.dropoff_address}</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground pt-0.5" data-testid={`ride-pax-mobile-${ride.id}`}>
          <Users className="w-3 h-3 flex-shrink-0" />
          <span>{ride.passenger_count ? `${ride.passenger_count} passenger${ride.passenger_count > 1 ? "s" : ""}` : "— passengers"}</span>
        </div>
      </div>
      {ride.completed_at && (
        <div className="text-[11px] text-muted-foreground mb-3" data-testid={`ride-completed-mobile-${ride.id}`}>
          Completed: {fmtCompleted(ride.completed_at)}
        </div>
      )}
      {(assignable || stuck || cancellable || completable || refundable) && (
        <div className="flex gap-2 flex-wrap">
          {assignable && (
            <button
              onClick={onOpenAssign}
              className="flex-1 text-xs py-2 bg-[#002FA7] hover:bg-[#001f6e] text-white font-semibold transition-colors flex items-center justify-center gap-1.5"
              data-testid={`assign-mobile-${ride.id}`}
            >
              <UserPlus className="w-3 h-3" /> {ride.status === "accepted" ? "Reassign" : "Assign"}
            </button>
          )}
          {stuck && (
            <button
              onClick={() => onRedispatch(ride.id)}
              className="flex-1 text-xs py-2 bg-[#FFC107] hover:bg-[#e0a900] text-black font-semibold transition-colors flex items-center justify-center gap-1.5"
              data-testid={`redispatch-mobile-${ride.id}`}
            >
              <RotateCw className="w-3 h-3" /> Auto re-dispatch
            </button>
          )}
          {completable && (
            <button
              onClick={() => onForceComplete(ride.id)}
              className="flex-1 text-xs py-2 bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold transition-colors flex items-center justify-center gap-1.5"
              data-testid={`force-complete-mobile-${ride.id}`}
              title="Force-complete — preserves fare and activates passenger payment"
            >
              <CheckCircle2 className="w-3 h-3" /> Force complete
            </button>
          )}
          {cancellable && (
            <button
              onClick={() => onForceCancel(ride.id)}
              className="flex-1 text-xs py-2 border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors flex items-center justify-center gap-1.5"
              data-testid={`force-cancel-mobile-${ride.id}`}
            >
              <X className="w-3 h-3" /> Force-cancel
            </button>
          )}
          {refundable && (
            <button
              onClick={onOpenRefund}
              className="flex-1 text-xs py-2 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white font-semibold transition-colors flex items-center justify-center gap-1.5"
              data-testid={`refund-mobile-${ride.id}`}
            >
              <RotateCcw className="w-3 h-3" /> Refund
            </button>
          )}
        </div>
      )}
    </div>
  );
};
